import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { todayLocalDateStr } from "@/lib/dateUtils";
import {
  Flame,
  Zap,
  Check,
  CalendarDays,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Lock,
  Loader2,
} from "lucide-react";
import CheckInCalendarModal from "./CheckInCalendarModal";
import { normaliseTier } from "@/lib/membershipUtils";
import {
  format,
  getISOWeek,
  getYear,
  startOfWeek,
  subDays,
  addDays,
} from "date-fns";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const MEMBERSHIP_BASE_URL =
  process.env.REACT_APP_MEMBERSHIP_URL || "https://gotafitness.com";
const FALLBACK_MEMBERSHIP_URL = `${MEMBERSHIP_BASE_URL}/membership`;
const SSO_REFRESH_MS = 4 * 60 * 1000;

function buildSsoUrl(ssoToken) {
  if (!ssoToken) return FALLBACK_MEMBERSHIP_URL;
  return `${MEMBERSHIP_BASE_URL}/auth/exchange#auth_token=${encodeURIComponent(
    ssoToken
  )}&next=${encodeURIComponent("/membership")}`;
}

function getWeekKey(date = new Date()) {
  const week = getISOWeek(date);
  const year = getYear(date);
  return `${year}-W${String(week).padStart(2, "0")}`;
}

function calcStreak(checkIns, userId) {
  if (!checkIns.length) return 0;
  const userCheckins = checkIns.filter((c) => c.user_id === userId);
  if (!userCheckins.length) return 0;
  const checkedDates = new Set(
    userCheckins.map((c) => c.checked_in_date).filter(Boolean)
  );
  if (!checkedDates.size) return 0;
  let streak = 0;
  let current = new Date();
  const todayKey = format(current, "yyyy-MM-dd");
  // If today isn't checked, start counting from yesterday
  if (!checkedDates.has(todayKey)) {
    current = subDays(current, 1);
  }
  while (checkedDates.has(format(current, "yyyy-MM-dd"))) {
    streak++;
    current = subDays(current, 1);
  }
  return streak;
}

export default function WeeklyCheckIn() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const [showCalendar, setShowCalendar] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const [justClaimed, setJustClaimed] = useState(false);
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);
  const containerRef = useRef(null);

  const currentWeekKey = getWeekKey();
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const hasCheckInAccess =
    normaliseTier(user?.membership_type) === "elite_coaching";

  // Pre-mint SSO token for membership overlay
  const [ssoUrl, setSsoUrl] = useState(FALLBACK_MEMBERSHIP_URL);
  useEffect(() => {
    if (hasCheckInAccess || !user) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const tok = await base44.auth.ssoToken();
        if (!cancelled) setSsoUrl(buildSsoUrl(tok));
      } catch (_) {
        if (!cancelled) setSsoUrl(FALLBACK_MEMBERSHIP_URL);
      }
    };
    refresh();
    const id = setInterval(refresh, SSO_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [hasCheckInAccess, user]);

  const { data: checkIns = [] } = useQuery({
    queryKey: ["checkins"],
    queryFn: () => base44.entities.CheckIn.list("-checked_in_date", 200),
  });

  const checkedDates = useMemo(
    () =>
      new Set(
        checkIns
          .filter((c) => c.user_id === user?.id)
          .map((c) => c.checked_in_date)
      ),
    [checkIns, user?.id]
  );

  const todayStr = todayLocalDateStr();
  const checkedInToday = checkedDates.has(todayStr);
  const streak = user ? calcStreak(checkIns, user.id) : 0;

  const mutation = useMutation({
    mutationFn: (dateOverride) =>
      base44.entities.CheckIn.create({
        user_id: user.id,
        week_key: currentWeekKey,
        checked_in_date: dateOverride || todayStr,
      }),
    onSuccess: (newCheckIn) => {
      // Optimistically add new check-in to cache so streak and indicator update instantly
      queryClient.setQueryData(["checkins"], (old = []) => [...old, newCheckIn]);
      queryClient.invalidateQueries({ queryKey: ["checkins"] });
      setJustClaimed(true);

      // Subtle haptic response on supported mobile devices
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        try {
          navigator.vibrate([40, 60, 40]);
        } catch (_) {}
      }

      setTimeout(() => setJustClaimed(false), 4000);
    },
    onError: (err) => {
      console.error("Failed to claim daily check-in:", err);
    },
  });

  const handleClaim = () => {
    if (!hasCheckInAccess || checkedInToday || mutation.isPending) return;
    mutation.mutate(todayStr);
  };

  const handleDayClick = (dateStr) => {
    if (dateStr === todayStr) {
      handleClaim();
    }
  };

  // Build the 7 days of the current week (Monday to Sunday)
  const dayCells = useMemo(() => {
    return DAYS.map((label, i) => {
      const date = addDays(weekStart, i);
      const dateStr = todayLocalDateStr(date);
      const checked = checkedDates.has(dateStr);
      const isCurrentDay = dateStr === todayStr;
      const isFuture = dateStr > todayStr;
      const isMissed = !checked && !isCurrentDay && !isFuture;
      const dayNum = format(date, "d");
      return {
        label,
        date,
        dateStr,
        checked,
        isCurrentDay,
        isFuture,
        isMissed,
        dayNum,
      };
    });
  }, [weekStart, todayStr, checkedDates]);

  // Motivational message based on streak and check-in status
  const motivationalMessage = useMemo(() => {
    if (checkedInToday) {
      if (streak >= 100) return "100+ Days! Legendary dedication.";
      if (streak >= 30) return "30-Day Milestone reached! Unstoppable discipline.";
      if (streak >= 14) return "Two-week streak! Consistency is your superpower.";
      if (streak >= 7) return "Full 7-day streak locked in! Incredible work.";
      return "You showed up today. Keep it going!";
    }
    if (streak > 0) {
      return "One more check-in keeps you moving.";
    }
    return "Start your streak today — every day counts!";
  }, [checkedInToday, streak]);

  // Handle ?action=check-in arrival from BottomNav
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") !== "check-in") return undefined;

    if (hasCheckInAccess && !checkedInToday && !mutation.isPending) {
      mutation.mutate(todayStr);
    }
    const el = containerRef.current;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlight(true);
      const t = setTimeout(() => setHighlight(false), 1600);
      params.delete("action");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : "",
        },
        { replace: true }
      );
      return () => clearTimeout(t);
    }
    return undefined;
  }, [
    location.search,
    location.pathname,
    navigate,
    hasCheckInAccess,
    checkedInToday,
    mutation,
    todayStr,
  ]);

  const isAchievementState = checkedInToday;

  return (
    <div
      ref={containerRef}
      data-testid="weekly-checkin-module"
      className={`relative rounded-2xl p-4 sm:p-5 text-white transition-all duration-500 space-y-3.5 ${
        highlight
          ? "ring-2 ring-sky-400 shadow-[0_0_30px_-4px_rgba(56,189,248,0.5)]"
          : isAchievementState
          ? "border-emerald-500/35 shadow-[0_0_10px_rgba(16,185,129,0.16),0_0_6px_rgba(245,158,11,0.10)]"
          : "shadow-lg shadow-black/40"
      }`}
      style={{
        background: isAchievementState
          ? "radial-gradient(ellipse at top left, #0A261B 0%, #061814 55%, #050B12 100%)"
          : "radial-gradient(ellipse at top, #0D1B2E 0%, #070D18 100%)",
        border: isAchievementState
          ? "1px solid rgba(16, 185, 129, 0.45)"
          : "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      {/* 1. COLLAPSED ACHIEVEMENT STATE (WHEN CHECKED IN) */}
      {checkedInToday && !isManuallyExpanded ? (
        <div className="space-y-2.5">
          {/* Header: Achievement Unlocked · Checked In */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-400 animate-pulse motion-reduce:animate-none" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-amber-300 drop-shadow-[0_0_4px_rgba(245,158,11,0.3)]">
                ACHIEVEMENT UNLOCKED · CHECKED IN
              </span>
            </div>

            <button
              type="button"
              onClick={() => setShowCalendar(true)}
              data-testid="view-checkin-history"
              className="flex items-center gap-1 text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <CalendarDays className="w-3 h-3 text-emerald-400" />
              <span>History</span>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </button>
          </div>

          {/* Collapsed Main Row: Streak Flame + Count, Checked In Badge, Expand Chevron */}
          <div className="flex items-center justify-between gap-3 pt-0.5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/40 text-emerald-400 flex items-center justify-center shrink-0 shadow-[0_0_8px_rgba(16,185,129,0.2)]">
                <Flame className="w-5 h-5 fill-emerald-400/30 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg sm:text-xl font-display font-black text-white tracking-tight leading-tight">
                    {streak} {streak === 1 ? "DAY" : "DAYS"} STREAK
                  </h3>
                  {justClaimed && [7, 14, 30, 100].includes(streak) && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40 animate-bounce motion-reduce:animate-none">
                      {streak}D Milestone!
                    </span>
                  )}
                </div>
                <p className="text-xs text-emerald-200/80 truncate">
                  {motivationalMessage}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <div className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500/25 to-amber-500/20 border border-emerald-400/50 text-emerald-300 text-xs font-black uppercase tracking-wider flex items-center gap-1.5 shadow-[0_0_8px_rgba(16,185,129,0.18)]">
                <Check className="w-3.5 h-3.5 stroke-[3] text-emerald-400" />
                <span>CHECKED IN</span>
              </div>

              <button
                type="button"
                onClick={() => setIsManuallyExpanded(true)}
                title="Show weekly breakdown"
                aria-label="Expand weekly breakdown"
                className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-slate-300 hover:text-white transition"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* EXPANDED STATE (BEFORE CHECK-IN OR MANUALLY EXPANDED) */
        <>
          {/* Header Row */}
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                {isAchievementState && (
                  <Sparkles className="w-3 h-3 text-amber-400 animate-pulse motion-reduce:animate-none" />
                )}
                <p
                  className={`text-[10px] sm:text-[11px] font-black uppercase tracking-widest ${
                    isAchievementState
                      ? "text-amber-300 drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]"
                      : "text-sky-400"
                  }`}
                >
                  {isAchievementState
                    ? "ACHIEVEMENT UNLOCKED · CHECKED IN"
                    : "YOUR DAILY STREAK"}
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <div
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center border transition-all ${
                    isAchievementState
                      ? "bg-emerald-500/20 border-emerald-400/40 text-emerald-400 shadow-[0_0_18px_rgba(16,185,129,0.4)]"
                      : streak > 0
                      ? "bg-sky-500/15 border-sky-400/40 text-sky-400 shadow-[0_0_18px_rgba(56,189,248,0.4)]"
                      : "bg-white/5 border-white/10 text-slate-500"
                  }`}
                >
                  <Flame
                    className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform ${
                      isAchievementState
                        ? "fill-emerald-400/30 text-emerald-400"
                        : streak > 0
                        ? "fill-sky-400/30 text-sky-400"
                        : ""
                    } ${justClaimed ? "scale-125 duration-300" : ""}`}
                  />
                </div>
                <h2 className="text-xl sm:text-2xl font-display font-black tracking-tight text-white uppercase">
                  {streak} {streak === 1 ? "DAY" : "DAYS"} STREAK
                </h2>
              </div>
              <p className={`text-xs pt-0.5 ${isAchievementState ? "text-emerald-200/80" : "text-slate-300"}`}>
                {motivationalMessage}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {/* Milestone Celebration Banner */}
              {justClaimed && [7, 14, 30, 100].includes(streak) && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[11px] sm:text-xs font-bold animate-bounce motion-reduce:animate-none shadow-[0_0_20px_rgba(245,158,11,0.25)] shrink-0">
                  <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
                  <span>{streak}-Day Milestone!</span>
                </div>
              )}

              {/* Collapse button if manually expanded */}
              {isAchievementState && isManuallyExpanded && (
                <button
                  type="button"
                  onClick={() => setIsManuallyExpanded(false)}
                  title="Collapse"
                  aria-label="Collapse weekly view"
                  className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold text-slate-300 flex items-center gap-1 transition"
                >
                  <span>Collapse</span>
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* 2. COMPACT WEEKLY CHECK-IN INDICATORS */}
          <div
            className={`grid grid-cols-7 gap-1.5 sm:gap-2 py-1 ${
              !hasCheckInAccess ? "pointer-events-none select-none opacity-40" : ""
            }`}
            aria-hidden={!hasCheckInAccess}
          >
            {dayCells.map(
              (
                { label, checked, isCurrentDay, isFuture, isMissed, dayNum, dateStr },
                i
              ) => {
                let circleClass = "";
                let content = null;

                if (isCurrentDay && checked) {
                  circleClass =
                    "bg-emerald-500 border border-emerald-400 text-white shadow-[0_0_14px_rgba(16,185,129,0.45)] animate-in zoom-in-90 duration-300 motion-reduce:animate-none";
                  content = <Check className="w-3.5 h-3.5 stroke-[3]" />;
                } else if (checked) {
                  circleClass =
                    "bg-sky-500 border border-sky-400 text-white shadow-[0_0_12px_rgba(56,189,248,0.4)]";
                  content = <Check className="w-3.5 h-3.5 stroke-[3]" />;
                } else if (isCurrentDay && !checked) {
                  circleClass =
                    "ring-2 ring-sky-400 ring-offset-2 ring-offset-[#0A1220] bg-sky-500/15 border border-sky-400 text-sky-300 animate-pulse motion-reduce:animate-none";
                  content = <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />;
                } else if (isMissed) {
                  circleClass =
                    "bg-white/[0.04] border border-white/10 text-white/20";
                  content = null;
                } else {
                  circleClass =
                    "bg-white/[0.02] border border-white/5 text-white/15";
                  content = null;
                }

                return (
                  <div
                    key={i}
                    className="flex flex-col items-center gap-1 min-w-0"
                  >
                    <span
                      className={`text-[9px] sm:text-[10px] font-bold tracking-wider uppercase ${
                        isCurrentDay ? "text-sky-400 font-extrabold" : "text-slate-400"
                      }`}
                    >
                      {label}
                    </span>

                    <button
                      type="button"
                      onClick={() => handleDayClick(dateStr)}
                      disabled={!hasCheckInAccess || !isCurrentDay || checkedInToday}
                      data-testid={`checkin-day-${dateStr}`}
                      aria-label={`${label} ${dayNum}`}
                      className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all ${circleClass} ${
                        isCurrentDay && !checkedInToday
                          ? "cursor-pointer hover:scale-105 active:scale-95"
                          : "cursor-default"
                      }`}
                    >
                      {content}
                    </button>

                    <span
                      className={`text-[10px] font-mono ${
                        isCurrentDay
                          ? "text-sky-300 font-bold"
                          : "text-slate-500 font-medium"
                      }`}
                    >
                      {dayNum}
                    </span>
                  </div>
                );
              }
            )}
          </div>

          {/* 3. CLAIM TODAY'S CHECK-IN BUTTON VS. COMPLETED STATE */}
          <div
            className={
              !hasCheckInAccess ? "pointer-events-none select-none opacity-40" : ""
            }
          >
            {checkedInToday ? (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 sm:p-3.5 text-center space-y-1 relative overflow-hidden shadow-[0_0_25px_rgba(16,185,129,0.12)] animate-in fade-in zoom-in-95 duration-300">
                <div className="flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-400">
                  <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-amber-300 font-bold">DAILY ACHIEVEMENT UNLOCKED</span>
                </div>
                <h3 className="text-base sm:text-lg font-display font-black text-white tracking-tight">
                  CHECK-IN COMPLETE!
                </h3>
                <p className="text-xs text-slate-300">
                  Another day of consistency. Nice work.
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleClaim}
                disabled={!hasCheckInAccess || mutation.isPending}
                data-testid="claim-checkin-btn"
                className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-extrabold text-sm tracking-wide shadow-[0_0_20px_rgba(14,165,233,0.35)] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                    <span>Recording Check-In...</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 fill-white text-white" />
                    <span>Claim Today's Check-In</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* 4. VIEW CHECK-IN HISTORY (REUSES EXISTING MODAL) */}
          <button
            type="button"
            onClick={() => setShowCalendar(true)}
            data-testid="view-checkin-history"
            className="w-full flex items-center justify-center gap-1.5 pt-0.5 text-xs font-semibold text-slate-400 hover:text-sky-300 transition-colors"
          >
            <CalendarDays className="w-3.5 h-3.5 text-sky-400" />
            <span>View Check-In History</span>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
          </button>
        </>
      )}

      {/* REUSED EXISTING CHECK-IN CALENDAR MODAL */}
      <CheckInCalendarModal
        open={showCalendar}
        onOpenChange={setShowCalendar}
        checkedDates={checkedDates}
      />

      {/* NON-ELITE COACHING MEMBERSHIP OVERLAY */}
      {!hasCheckInAccess && (
        <a
          href={ssoUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="checkin-upgrade-overlay"
          className="absolute inset-0 flex flex-col items-center justify-center text-center rounded-2xl transition-all hover:scale-[1.005] group z-10 p-4"
          style={{
            background: "rgba(2,8,23,0.85)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid rgba(251,191,36,0.35)",
            boxShadow: "0 0 24px rgba(251,191,36,0.08)",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4" style={{ color: "#FBBF24" }} />
            <span
              className="text-xs sm:text-sm font-semibold uppercase tracking-wider"
              style={{ color: "#FBBF24" }}
            >
              Elite Coaching only
            </span>
          </div>
          <p className="text-xs sm:text-sm text-white/85 leading-snug max-w-sm px-4">
            Upgrade to{" "}
            <span className="font-semibold" style={{ color: "#FBBF24" }}>
              Elite Coaching
            </span>{" "}
            to track your weekly check-ins and build your streak.
          </p>
          <span className="mt-3 inline-flex items-center gap-1 text-[11px] sm:text-xs font-medium text-white/60 group-hover:text-white transition-colors">
            Manage membership
            <span aria-hidden="true">→</span>
          </span>
        </a>
      )}
    </div>
  );
}